import { quizQuestions, computeQuizResult } from "@/mocks/fxproCompareData";
import { useState } from "react";
import { brokers } from "@/data/brokers";

export default function BrokerQuiz() {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<{ broker: "xm" | "fxpro"; percentage: number; reason: string } | null>(null);

  const handleAnswer = (questionId: string, value: string) => {
    const newAnswers = { ...answers, [questionId]: value };
    setAnswers(newAnswers);
    if (currentStep < quizQuestions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setResult(computeQuizResult(newAnswers));
    }
  };

  const handleReset = () => {
    setCurrentStep(0);
    setAnswers({});
    setResult(null);
  };

  const currentQ = quizQuestions[currentStep];

  return (
    <section className="py-20 md:py-28 bg-[#111827]">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/20 mb-5">
            <i className="ri-brain-line text-[#F59E0B]"></i>
            <span className="text-xs font-bold text-[#F59E0B] uppercase tracking-wider">Smart Quiz</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-3">Still Undecided?</h2>
          <p className="text-[#94A3B8] text-sm md:text-base max-w-lg mx-auto">
            Your capital size. Your trading style. Your priorities. Tell us these things and we'll recommend whether XM or FxPro fits you better.
          </p>
        </div>

        {!result ? (
          <div className="bg-[#1A2235] border border-[#2A3345] rounded-2xl p-8 md:p-10">
            {/* Progress bar */}
            <div className="flex items-center gap-2 mb-8">
              {quizQuestions.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full flex-1 transition-all duration-300 ${
                    i < currentStep ? "bg-[#0D9488]" : i === currentStep ? "bg-[#0D9488]/60" : "bg-[#2A3345]"
                  }`}
                />
              ))}
            </div>

            <h3 className="text-lg md:text-xl font-bold text-[#F8FAFC] mb-6">{currentQ.question}</h3>

            <div className="space-y-3">
              {currentQ.options.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => handleAnswer(currentQ.id, opt.value)}
                  className="w-full text-left p-4 rounded-xl bg-[#111827] border border-[#2A3345] hover:border-[#0D9488]/50 hover:bg-[#0D9488]/5 text-sm text-[#94A3B8] hover:text-[#F8FAFC] transition-all whitespace-normal"
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-[#1A2235] border border-[#2A3345] rounded-2xl p-8 md:p-10 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#0D9488] flex items-center justify-center">
              <img src={result.broker === "xm" ? "/images/xm.jpg" : "/images/fxpro.jpg"} alt={result.broker} className="w-14 h-14 rounded-full object-cover" />
            </div>

            <h3 className="text-xl md:text-2xl font-bold text-[#F8FAFC] mb-2">
              You are{" "}
              <span className={result.broker === "xm" ? "text-[#8B5CF6]" : "text-[#14B8A6]"}>
                {result.percentage}%
              </span>{" "}
              matched with {result.broker === "xm" ? "XM" : "FxPro"}
            </h3>

            {/* Match bar */}
            <div className="flex items-center gap-4 max-w-md mx-auto my-6">
              <span className="text-xs font-bold text-[#8B5CF6] w-12 text-right">XM</span>
              <div className="flex-1 h-3 bg-[#2A3345] rounded-full overflow-hidden flex">
                <div
                  className="h-full bg-[#7C3AED] transition-all duration-700"
                  style={{ width: `${result.broker === "xm" ? result.percentage : 100 - result.percentage}%` }}
                />
              </div>
              <span className="text-xs font-bold text-[#14B8A6] w-16">FxPro</span>
            </div>

            <p className="text-sm text-[#94A3B8] leading-relaxed mb-8 max-w-lg mx-auto">{result.reason}</p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={result.broker === "xm" ? brokers.xm.href : brokers.fxpro.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto inline-flex items-center gap-2 px-8 py-3.5 bg-[#0D9488] hover:bg-[#14B8A6] text-white text-sm font-bold rounded-lg transition-all whitespace-nowrap"
              >
                Open {result.broker === "xm" ? "XM" : "FxPro"} Account
                <i className="ri-arrow-right-up-line"></i>
              </a>
              <button
                onClick={handleReset}
                className="w-full sm:w-auto inline-flex items-center gap-2 px-6 py-3.5 border border-[#374151] hover:border-[#475569] text-[#94A3B8] hover:text-[#F8FAFC] text-sm rounded-lg transition-all whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                Retake Quiz
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}